import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Settings, User, CreditCard, Mail, Send, Loader2, Save, MessageSquare, ArrowRight, Shield, AlertTriangle,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUPPORT_EMAIL = "businessgenie.bg@gmail.com";

const CATEGORIES = [
  { value: "general", label: "General Question" },
  { value: "billing", label: "Billing / Subscription" },
  { value: "bug", label: "Bug Report" },
  { value: "feature", label: "Feature Request" },
  { value: "other", label: "Other" },
];

export default function AccountSettings() {
  const { user, setUser } = useAuth();
  const [savingProfile, setSavingProfile] = useState(false);
  const [profile, setProfile] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
  });
  const [subscription, setSubscription] = useState(null);
  const [loadingSub, setLoadingSub] = useState(true);
  const [contactForm, setContactForm] = useState({
    category: "general",
    subject: "",
    message: "",
  });
  const [sendingContact, setSendingContact] = useState(false);
  // iter138 — fix pre-existing crash: tenantPrefs was referenced but never defined
  const [tenantPrefs, setTenantPrefs] = useState(null);
  const togglePref = async (key, value) => {
    try {
      const r = await axios.put(`${API}/me/tenant-prefs`, { [key]: value }, { headers });
      setTenantPrefs(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update preference");
    }
  };

  const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };

  useEffect(() => {
    setProfile({
      name: user?.name || "",
      email: user?.email || "",
      phone: user?.phone || "",
    });
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/subscriptions/usage`, { headers });
        if (!cancelled) setSubscription(res.data);
      } catch {
        // tenant-less users get no subscription — that's fine
      } finally {
        if (!cancelled) setLoadingSub(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // iter138 — load tenant preferences for the Business Preferences card
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/me/tenant-prefs`, { headers });
        setTenantPrefs(r.data);
      } catch {
        setTenantPrefs({ no_tenant: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveProfile = async () => {
    if (!profile.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSavingProfile(true);
    try {
      const res = await axios.put(
        `${API}/auth/me`,
        { name: profile.name, phone: profile.phone },
        { headers }
      );
      toast.success("Profile updated");
      if (setUser && res.data) setUser({ ...user, ...res.data });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const sendContact = async (e) => {
    e?.preventDefault?.();
    if (!contactForm.subject.trim() || !contactForm.message.trim()) {
      toast.error("Please fill in subject and message");
      return;
    }
    setSendingContact(true);
    try {
      const res = await axios.post(
        `${API}/support/contact`,
        {
          category: contactForm.category,
          subject: contactForm.subject,
          message: contactForm.message,
        },
        { headers }
      );
      toast.success(res.data?.message || "Message sent");
      setContactForm({ category: "general", subject: "", message: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send message");
    } finally {
      setSendingContact(false);
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-4xl mx-auto" data-testid="account-settings-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-3">
          <Settings className="h-8 w-8 text-primary" />
          Account Settings
        </h1>
        <p className="text-muted-foreground mt-1">
          Manage your profile, subscription, and contact Business Genie support
        </p>
      </div>

      <Tabs defaultValue="profile" className="space-y-4">
        <TabsList>
          <TabsTrigger value="profile" data-testid="tab-profile">
            <User className="h-4 w-4 mr-2" /> Profile
          </TabsTrigger>
          <TabsTrigger value="subscription" data-testid="tab-subscription">
            <CreditCard className="h-4 w-4 mr-2" /> Subscription
          </TabsTrigger>
          <TabsTrigger value="contact" data-testid="tab-contact">
            <MessageSquare className="h-4 w-4 mr-2" /> Contact Support
          </TabsTrigger>
          <TabsTrigger value="danger" data-testid="tab-danger" className="text-red-600 data-[state=active]:text-red-700">
            <AlertTriangle className="h-4 w-4 mr-2" /> Danger Zone
          </TabsTrigger>
        </TabsList>

        {/* Profile */}
        <TabsContent value="profile" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Personal Information</CardTitle>
              <CardDescription>Update your name and contact details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Full Name *</Label>
                  <Input
                    value={profile.name}
                    onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                    data-testid="profile-name-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Email</Label>
                  <Input
                    value={profile.email}
                    disabled
                    data-testid="profile-email-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Email changes require verification — contact support if you need to switch.
                  </p>
                </div>
              </div>
              <div className="space-y-1">
                <Label>Phone</Label>
                <Input
                  value={profile.phone}
                  onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                  placeholder="+1 (555) 123-4567"
                  data-testid="profile-phone-input"
                />
              </div>
              <div className="flex justify-end">
                <Button onClick={saveProfile} disabled={savingProfile} data-testid="save-profile-btn">
                  {savingProfile ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Changes
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" /> Security
              </CardTitle>
              <CardDescription>Password &amp; account security</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                To change your password, sign out and use "Forgot password" on the login page,
                or contact support if you need help.
              </p>
            </CardContent>
          </Card>

          {/* Phase 19.28 — Tenant Preferences */}
          {tenantPrefs && !tenantPrefs.no_tenant && (
            <Card data-testid="tenant-prefs-card">
              <CardHeader>
                <CardTitle>Business Preferences</CardTitle>
                <CardDescription>Defaults applied across this business account</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-start justify-between gap-3 p-3 rounded-md bg-muted/30">
                  <div className="min-w-0">
                    <Label className="text-sm font-medium">
                      Auto-send portal invite on bulk client import
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      When <strong>ON</strong>, every newly bulk-imported client automatically receives a portal-invite email after the batch is created. When <strong>OFF</strong> (default), you'll see a "Send portal invites" button after each batch so you can decide per-import.
                    </p>
                  </div>
                  <Switch
                    checked={!!tenantPrefs.auto_send_portal_invite_on_bulk_import}
                    onCheckedChange={(v) => togglePref("auto_send_portal_invite_on_bulk_import", v)}
                    data-testid="auto-invite-bulk-toggle"
                  />
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Subscription */}
        <TabsContent value="subscription" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Your Subscription</CardTitle>
              <CardDescription>Plan, limits, and billing</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {loadingSub ? (
                <div className="py-6 text-center">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground mx-auto" />
                </div>
              ) : !subscription || subscription?.no_tenant ? (
                <div className="text-sm text-muted-foreground">
                  No active subscription is attached to this account.
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-3 p-4 rounded-lg border">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">Current Plan</p>
                      <p className="text-xl font-bold capitalize" data-testid="sub-plan-name">
                        {subscription.plan_name || subscription.plan_id || "—"}
                      </p>
                    </div>
                    <Badge variant="outline" className="capitalize" data-testid="sub-status-badge">
                      {subscription.subscription_status || "—"}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="p-3 rounded border text-center">
                      <p className="text-xs text-muted-foreground">Employees</p>
                      <p className="text-lg font-bold">
                        {subscription.usage?.employees ?? 0} / {subscription.max_employees === -1 ? "∞" : (subscription.max_employees ?? "—")}
                      </p>
                    </div>
                    <div className="p-3 rounded border text-center">
                      <p className="text-xs text-muted-foreground">Clients</p>
                      <p className="text-lg font-bold">
                        {subscription.usage?.clients ?? 0} / {subscription.max_clients === -1 ? "∞" : (subscription.max_clients ?? "—")}
                      </p>
                    </div>
                    <div className="p-3 rounded border text-center">
                      <p className="text-xs text-muted-foreground">Users</p>
                      <p className="text-lg font-bold">
                        {subscription.usage?.users ?? 0} / {subscription.max_users === -1 ? "∞" : (subscription.max_users ?? "—")}
                      </p>
                    </div>
                    <div className="p-3 rounded border text-center">
                      <p className="text-xs text-muted-foreground">Quotes /mo</p>
                      <p className="text-lg font-bold">
                        {subscription.usage?.quotes_this_month ?? 0} / {subscription.max_quotes_per_month === -1 ? "∞" : (subscription.max_quotes_per_month ?? "—")}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              <div className="flex justify-end pt-2">
                <Link to="/manage-subscription">
                  <Button data-testid="manage-subscription-btn">
                    <CreditCard className="h-4 w-4 mr-2" /> Manage Subscription
                    <ArrowRight className="h-3 w-3 ml-2" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contact Business Genie */}
        <TabsContent value="contact" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Contact Business Genie
              </CardTitle>
              <CardDescription>
                Send a question or comment to <span className="font-mono text-foreground">{SUPPORT_EMAIL}</span>.
                We typically reply within 1 business day.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form className="space-y-4" onSubmit={sendContact} data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label>Category</Label>
                    <Select
                      value={contactForm.category}
                      onValueChange={(v) => setContactForm({ ...contactForm, category: v })}
                    >
                      <SelectTrigger data-testid="contact-category-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map((c) => (
                          <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label>Replying to</Label>
                    <Input value={user?.email || ""} disabled />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label>Subject *</Label>
                  <Input
                    value={contactForm.subject}
                    onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                    placeholder="What's this about?"
                    data-testid="contact-subject-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Message *</Label>
                  <Textarea
                    rows={6}
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    placeholder="Tell us what you need..."
                    data-testid="contact-message-input"
                  />
                </div>
                <div className="flex justify-end">
                  <Button type="submit" disabled={sendingContact} data-testid="send-contact-btn">
                    {sendingContact ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4 mr-2" />
                    )}
                    Send Message
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Danger Zone — reset account back to a blank slate */}
        <TabsContent value="danger" className="space-y-4">
          <DangerZone user={user} />
        </TabsContent>
      </Tabs>
    </div>
  );
}


function DangerZone({ user }) {
  const [confirm, setConfirm] = useState("");
  const [resetting, setResetting] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [purging, setPurging] = useState(false);
  const [preview, setPreview] = useState(null);
  const [businessName, setBusinessName] = useState("");
  const [pendingSnapshot, setPendingSnapshot] = useState(null);

  const loadTenantInfo = async () => {
    try {
      const r = await axios.get(`${API}/tenant-admin/me`);
      setBusinessName(r?.data?.name || "");
      setPendingSnapshot(r?.data?.pending_snapshot || null);
    } catch { /* leave empty */ }
  };

  useEffect(() => {
    loadTenantInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const runReset = async () => {
    if (!confirm.trim()) {
      toast.error("Type your business name to confirm");
      return;
    }
    setResetting(true);
    try {
      const r = await axios.post(`${API}/tenant-admin/reset`, {
        confirm_phrase: confirm.trim(),
      });
      setPreview(r.data);
      toast.success(`Reset complete — ${r.data.rows_deleted} rows deleted. You have 48 hours to Undo.`);
      setConfirm("");
      await loadTenantInfo();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  const runUndo = async () => {
    setRestoring(true);
    try {
      const r = await axios.post(`${API}/tenant-admin/reset/undo`, {});
      toast.success(`Restored ${r.data.rows_restored} rows.`);
      setPendingSnapshot(null);
      setPreview(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Undo failed");
    } finally {
      setRestoring(false);
    }
  };

  const runPurge = async () => {
    if (!window.confirm("Immediately and permanently delete the snapshot? This CANNOT be undone.")) return;
    setPurging(true);
    try {
      await axios.post(`${API}/tenant-admin/reset/purge`, {});
      toast.success("Snapshot purged. Reset is now permanent.");
      setPendingSnapshot(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purge failed");
    } finally {
      setPurging(false);
    }
  };

  return (
    <Card className="border-red-300">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-red-700">
          <AlertTriangle className="h-5 w-5" /> Reset My Account
        </CardTitle>
        <CardDescription>
          Deletes <b>every</b> business record you have created — clients, employees, quotes, invoices, schedules,
          services, goods, inventory, ROEs, fleet vehicles, equipment, safety docs, payroll runs — and returns your
          account to a brand-new state. Your <b>login, subscription, and business profile</b> are kept.
          <br />
          <span className="text-red-600 font-medium">You have 48 hours to Undo before it becomes permanent.</span>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {pendingSnapshot && (
          <div className="rounded-md border border-amber-400 bg-amber-50 p-3 space-y-2" data-testid="pending-snapshot-banner">
            <p className="text-sm text-amber-900">
              <b>{pendingSnapshot.row_count.toLocaleString()} rows</b> deleted{" "}
              {new Date(pendingSnapshot.reset_at).toLocaleString()}.{" "}
              <b>{Math.round(pendingSnapshot.hours_remaining)} hours</b> left to Undo.
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={runUndo}
                disabled={restoring}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="danger-undo-btn"
              >
                {restoring && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Undo Reset
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={runPurge}
                disabled={purging}
                className="text-red-600 hover:bg-red-50"
                data-testid="danger-purge-btn"
              >
                {purging && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Make Permanent Now
              </Button>
            </div>
          </div>
        )}
        <div>
          <Label>Type your business name to confirm{businessName ? `: "${businessName}"` : ""}</Label>
          <Input
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder={businessName || "Your business name"}
            className="mt-1"
            data-testid="danger-confirm-input"
          />
        </div>
        <Button
          variant="destructive"
          onClick={runReset}
          disabled={resetting || !confirm.trim() || !!pendingSnapshot}
          data-testid="danger-reset-btn"
          title={pendingSnapshot ? "Undo or purge the pending snapshot first" : ""}
        >
          {resetting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          <AlertTriangle className="h-4 w-4 mr-2" />
          Reset My Account
        </Button>
        {preview && (
          <div className="rounded-md border bg-red-50 border-red-200 p-3 text-xs" data-testid="danger-reset-result">
            <p className="font-medium text-red-800 mb-2">
              Reset complete. Deleted {preview.rows_deleted} rows from {preview.tenant_name}.
            </p>
            <ul className="space-y-0.5 max-h-40 overflow-y-auto text-red-900/80">
              {preview.detail.map((d, i) => (
                <li key={i}>
                  {d.table}: {d.rows !== undefined ? `${d.rows} rows` : `error — ${d.error}`}
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
